import {
  Component,
  computed,
  effect,
  forwardRef,
  input,
  Input as NgInput,
  NgZone,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * Types supportés par le composant Input.
 * - Les types natifs HTML sont passés directement au DOM.
 * - "textarea"  → rendu en <textarea>
 * - "number"    → si thousandSeparator=true, rendu en type="text" avec formatage
 */
export type InputType =
  | 'text'
  | 'number'
  | 'email'
  | 'password'
  | 'tel'
  | 'url'
  | 'date'
  | 'datetime-local'
  | 'time'
  | 'month'
  | 'week'
  | 'search'
  | 'color'
  | 'range'
  | 'file'
  | 'textarea';

@Component({
  selector: 'app-input',
  imports: [CommonModule, FormsModule],
  templateUrl: './input.html',
  styleUrls: ['./input.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Input),
      multi: true,
    },
  ],
  standalone: true,
})
export class Input implements ControlValueAccessor {
  // ─── Inputs du composant ──────────────────────────────────────────────────

  label         = input.required<string>();
  type          = input.required<InputType>();
  name          = input<string>('');
  placeholder   = input<string>('');
  thousandSeparator = input<boolean>(false);
  rows          = input<number>(4);
  min           = input<string | number | null>(null);
  max           = input<string | number | null>(null);
  step          = input<string | number | null>(null);
  accept        = input<string | null>(null);   // pour type="file"
  multiple      = input<boolean>(false);         // pour type="file" / "email"
  autocomplete  = input<string>('off');

  // ─── Inputs legacy (binding direct [value] / [disabled]) ─────────────────

  @NgInput()
  set value(v: any) { this.writeValue(v); }

  @NgInput()
  set disabled(v: any) {
    this.isDisabled = v === '' || v === true || v === 'true' || !!v;
  }

  // ─── État interne ─────────────────────────────────────────────────────────

  /** Valeur affichée dans le DOM (potentiellement formatée) */
  protected _displayValue = signal<string>('');
  /** Valeur brute transmise au FormControl (jamais formatée) */
  private   _rawValue      = signal<string>('');

  isDisabled = false;

  /** Type réel appliqué sur l'élément <input> du DOM */
  readonly effectiveType = computed<string>(() =>
    this.thousandSeparator() && this.type() === 'number' ? 'text' : this.type()
  );

  /** Vrai si le composant doit rendre un <textarea> */
  readonly isTextarea = computed(() => this.type() === 'textarea');

  /** Vrai si c'est un type purement visuel/spécial sans gestion de valeur texte */
  readonly isRange  = computed(() => this.type() === 'range');
  readonly isColor  = computed(() => this.type() === 'color');
  readonly isFile   = computed(() => this.type() === 'file');

  // ─── Callbacks CVA ────────────────────────────────────────────────────────

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void         = () => {};

  constructor(private ngZone: NgZone) {}

  // ─── ControlValueAccessor ─────────────────────────────────────────────────

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this._rawValue.set('');
      this._displayValue.set('');
      return;
    }

    if (this.thousandSeparator() && this.type() === 'number') {
      const pure = this._toPureNumeric(String(value));
      this._rawValue.set(pure);
      this._displayValue.set(this._formatThousands(pure));
    } else {
      const str = String(value);
      this._rawValue.set(str);
      this._displayValue.set(str);
    }
  }

  registerOnChange(fn: any): void  { this._onChange  = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }
  setDisabledState(v: boolean): void { this.isDisabled = v; }

  // ─── Événements DOM ───────────────────────────────────────────────────────

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const inputValue = target.value;

    // ── Cas thousandSeparator ────────────────────────────────────────────
    if (this.thousandSeparator() && this.type() === 'number') {
      const inputEl       = target as HTMLInputElement;
      const cursorPos     = inputEl.selectionStart ?? inputValue.length;
      const oldFormatted  = this._displayValue();

      // Compter les espaces avant le curseur dans l'ancienne valeur
      const spacesBeforeCursor = (oldFormatted.substring(0, cursorPos).match(/ /g) ?? []).length;
      const pureCursorPos      = cursorPos - spacesBeforeCursor;

      const pure      = this._toPureNumeric(inputValue);
      const formatted = this._formatThousands(pure);

      this._rawValue.set(pure);
      this._displayValue.set(formatted);
      this._onChange(this._toNumber(pure));

      // Restaurer le curseur après re-render
      this.ngZone.runOutsideAngular(() =>
        requestAnimationFrame(() => {
          const spacesBeforePure = (formatted.substring(0, pureCursorPos).match(/ /g) ?? []).length;
          let newCursor = pureCursorPos + spacesBeforePure;

          // Si on a supprimé un espace, reculer d'un cran
          if (oldFormatted.length > formatted.length && oldFormatted[cursorPos - 1] === ' ') {
            newCursor--;
          }
          // Si on tombe sur un espace, avancer
          if (formatted[newCursor] === ' ' && newCursor < formatted.length) {
            newCursor++;
          }

          const final = Math.min(Math.max(newCursor, 0), formatted.length);
          inputEl.setSelectionRange(final, final);
        })
      );

    // ── Cas file (pas de valeur texte à propager) ────────────────────────
    } else if (this.type() === 'file') {
      const fileInput = target as HTMLInputElement;
      this._onChange(fileInput.files);

    // ── Cas standard ────────────────────────────────────────────────────
    } else {
      const cursorPos = (target as HTMLInputElement).selectionStart;
      this._rawValue.set(inputValue);
      this._displayValue.set(inputValue);
      this._onChange(this.type() === 'number' ? this._toNumber(inputValue) : inputValue);

      // Restaurer le curseur pour les types qui gèrent une position (texte, tel…)
      if (cursorPos !== null && this.effectiveType() !== 'date' && this.effectiveType() !== 'time') {
        this.ngZone.runOutsideAngular(() =>
          requestAnimationFrame(() => {
            const el = target as HTMLInputElement;
            const final = Math.min(cursorPos, inputValue.length);
            try { el.setSelectionRange(final, final); } catch (_) {}
          })
        );
      }
    }

    this._onTouched();
  }

  onBlur(): void {
    // Re-formater à la perte du focus (normalise les décimales, etc.)
    if (this.thousandSeparator() && this.type() === 'number') {
      const pure = this._toPureNumeric(this._displayValue());
      this._rawValue.set(pure);
      this._displayValue.set(this._formatThousands(pure));
      this._onChange(this._toNumber(pure));
    }
    this._onTouched();
  }

  // ─── Utilitaires de formatage ─────────────────────────────────────────────

  /**
   * Supprime tout caractère non numérique sauf le point décimal.
   * Gère les virgules comme séparateur décimal alternatif.
   * Conserve un seul point décimal maximum.
   */
  private _toPureNumeric(value: string): string {
    // Normaliser la virgule en point
    let v = value.replace(/,/g, '.');
    // Supprimer tout sauf chiffres et point
    v = v.replace(/[^\d.]/g, '');
    // Ne garder qu'un seul point décimal
    const parts = v.split('.');
    if (parts.length > 2) {
      v = parts[0] + '.' + parts.slice(1).join('');
    }
    return v;
  }

  /**
   * Formate un nombre pur en ajoutant des espaces comme séparateurs de milliers.
   * Fonctionne sur des valeurs de taille arbitraire (milliards, quadrillions…).
   * Ne passe jamais par Number() pour éviter toute perte de précision.
   */
  private _formatThousands(value: string): string {
    if (!value) return '';

    const [intPart, decPart] = value.split('.');
    const formattedInt = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return decPart !== undefined
      ? `${formattedInt}.${decPart}`
      : formattedInt;
  }

  private _toNumber(value: string): number | string {
    if (value === '') return '';
    const num = Number(value);
    return Number.isFinite(num) ? num : value;
  }

  // ─── Helpers template ─────────────────────────────────────────────────────

  /** Valeur affichée (lecture seule depuis le template) */
  get displayValue(): string {
    return this._displayValue();
  }
}